import { useCallback, useMemo } from 'react'
import {
  useCreateSettlementPolicyMutation,
  useUpdateSettlementPolicyMutation,
  useGetSettlementCounterpartiesQuery,
  useGetSettlementRecordsQuery,
  useGetSettlementPolicyQuery,
  useCreateSettlementPolicyBankAccountMutation,
  useUpdateSettlementPolicyBankAccountMutation,
  useDeleteSettlementPolicyBankAccountMutation,
} from 'store/api/merklebaseAPI'

export default function useSettlement({
  fetchCounterparties = true,
  fetchRecords = false,
  recordsFilters = {},
  policyId = null,
} = {}) {
  const counterpartiesQuery = useGetSettlementCounterpartiesQuery(undefined, {
    skip: !fetchCounterparties,
  })

  const recordsQuery = useGetSettlementRecordsQuery(recordsFilters, {
    skip: !fetchRecords,
  })

  const policyQuery = useGetSettlementPolicyQuery(policyId, {
    skip: !policyId,
  })

  const [createSettlementPolicyMutation] = useCreateSettlementPolicyMutation()
  const [updateSettlementPolicyMutation] = useUpdateSettlementPolicyMutation()
  const [createSettlementPolicyBankAccountMutation] = useCreateSettlementPolicyBankAccountMutation()
  const [updateSettlementPolicyBankAccountMutation] = useUpdateSettlementPolicyBankAccountMutation()
  const [deleteSettlementPolicyBankAccountMutation] = useDeleteSettlementPolicyBankAccountMutation()

  const counterparties = useMemo(() => counterpartiesQuery.data ?? [], [counterpartiesQuery.data])

  const records = useMemo(
    () => ({
      data: recordsQuery.data?.data ?? [],
      summary: recordsQuery.data?.summary ?? {},
      pagination: recordsQuery.data?.pagination ?? {},
    }),
    [recordsQuery.data]
  )

  const saveSettlementPolicy = useCallback(
    async (policy) => {
      if (policy.id) {
        const { id, ...policyData } = policy
        return updateSettlementPolicyMutation({ id, ...policyData }).unwrap()
      }
      return createSettlementPolicyMutation(policy).unwrap()
    },
    [createSettlementPolicyMutation, updateSettlementPolicyMutation]
  )

  const createBankAccount = useCallback(
    async (policyId, bankAccount) =>
      createSettlementPolicyBankAccountMutation({ policyId, ...bankAccount }).unwrap(),
    [createSettlementPolicyBankAccountMutation]
  )

  const updateBankAccount = useCallback(
    async (policyId, accountId, bankAccount) =>
      updateSettlementPolicyBankAccountMutation({ policyId, accountId, ...bankAccount }).unwrap(),
    [updateSettlementPolicyBankAccountMutation]
  )

  const deleteBankAccount = useCallback(
    async (policyId, accountId) =>
      deleteSettlementPolicyBankAccountMutation({ policyId, accountId }).unwrap(),
    [deleteSettlementPolicyBankAccountMutation]
  )

  return {
    counterparties: {
      data: counterparties,
      isLoading: counterpartiesQuery.isLoading,
    },
    records,
    policy: {
      data: policyQuery.data,
      isLoading: policyQuery.isLoading,
    },
    saveSettlementPolicy,
    createBankAccount,
    updateBankAccount,
    deleteBankAccount,
  }
}
